import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AppState } from '@app/state';
import {
  selectActiveUploadCount,
  selectAggregateProgress,
  selectFailedUploads,
  selectUploadQueue,
} from '@app/state/upload-queue/upload-queue.selectors';

import { UploadDetailSheetComponent } from './upload-detail-sheet.component';

/**
 * Always mounted from `app.component.html`, beside `<app-upload-progress>`,
 * so the queue stays visible on every page without a single page header
 * knowing it exists.
 *
 * Deliberately no `NgZone.run` here. The queue actions originate in Capacitor
 * plugin listeners, which native invokes outside Angular's zone, but the
 * re-entry happens at the dispatch source in `AppComponent.startUploadQueue`,
 * so everything downstream -- these fields and the detail sheet's `async`
 * pipe alike -- is already notified inside the zone. Re-adding it here would
 * only buy extra change-detection passes, and would still leave the modal,
 * which has no zone plumbing of its own, depending on this component being
 * subscribed first. `UploadProgressComponent` keeps its own arrangement; it
 * serves the separate legacy document path.
 *
 * One subscription, not three: all three figures are projections of the same
 * queue array, so deriving them together turns one dispatch into one field
 * update instead of three interleaved ones. The selectors' own projectors are
 * reused so the arithmetic stays defined in exactly one place.
 */
@Component({
  selector: 'app-upload-indicator',
  templateUrl: './upload-indicator.component.html',
  styleUrls: ['./upload-indicator.component.scss'],
})
export class UploadIndicatorComponent implements OnInit, OnDestroy {
  activeCount = 0;
  failedCount = 0;
  progress = 0;

  private subs = new Subscription();
  private detail: HTMLIonModalElement | null = null;
  private opening = false;

  constructor(
    private store$: Store<AppState>,
    private modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.subs.add(
      this.store$.pipe(select(selectUploadQueue)).subscribe(items => {
        this.activeCount = selectActiveUploadCount.projector(items);
        this.progress = selectAggregateProgress.projector(items);
        this.failedCount = selectFailedUploads.projector(items).length;
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  get visible(): boolean {
    return this.activeCount > 0 || this.failedCount > 0;
  }

  /**
   * A failure while others are still uploading is called out in the label
   * rather than allowed to wait for the queue to drain: the pill turns red
   * the moment anything fails, and the text has to explain why.
   */
  get label(): string {
    if (this.activeCount > 0) {
      const plural = this.activeCount === 1 ? '' : 's';
      const failed =
        this.failedCount > 0 ? ` (${this.failedCount} failed)` : '';
      return `Uploading ${this.activeCount} photo${plural}...${failed}`;
    }
    const one = this.failedCount === 1;
    return `${this.failedCount} upload${one ? '' : 's'} ${
      one ? 'needs' : 'need'
    } attention`;
  }

  /**
   * The pill lives outside `<ion-app>`, so the sheet never covers it and it
   * stays tappable while the sheet is up. A tap therefore toggles the one
   * sheet rather than stacking another. `opening` closes the gap between the
   * tap and `present()` resolving, so a quick double tap cannot slip a second
   * sheet through before `detail` is set.
   */
  async toggleDetail() {
    if (this.detail) {
      await this.detail.dismiss();
      return;
    }
    if (this.opening) {
      return;
    }
    this.opening = true;
    try {
      const modal = await this.modalCtrl.create({
        component: UploadDetailSheetComponent,
        breakpoints: [0, 0.5, 0.9],
        initialBreakpoint: 0.5,
      });
      this.detail = modal;
      void modal.onDidDismiss().then(() => {
        if (this.detail === modal) {
          this.detail = null;
        }
      });
      await modal.present();
    } finally {
      this.opening = false;
    }
  }
}
